import React, { useState } from 'react'
import { useSession, signOut } from '../../auth/betterAuthClient'
import { useAuth } from '../../auth/AuthProvider'
import { clearAuthToken } from '../../auth/guestAuth'
import { Icon } from '@flatfile/baseline'
import { useUserMetrics } from '../../hooks/useUserMetrics'

export const AccountPage: React.FC = () => {
  const { data: session } = useSession()
  const { isGuest, user } = useAuth()
  const [isSigningOut, setIsSigningOut] = useState(false)
  const { metrics, loading: metricsLoading } = useUserMetrics()

  const handleSignOut = async () => {
    setIsSigningOut(true)
    try {
      if (isGuest) {
        clearAuthToken()
      } else {
        await signOut()
      }
      window.location.href = '/'
    } catch (error) {
      console.error('Sign out error:', error)
      setIsSigningOut(false)
    }
  }

  return (
    <div className='max-w-2xl mx-auto p-6'>
      <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
        {/* Header */}
        <div className='bg-gray-50 px-6 py-4 border-b border-gray-200'>
          <h1 className='text-2xl font-semibold text-gray-900'>
            Account Settings
          </h1>
          <p className='text-sm text-gray-600 mt-1'>
            Manage your account information and preferences
          </p>
        </div>

        {/* Account Info */}
        <div className='p-6 space-y-6'>
          {isGuest ? (
            <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
              <div className='flex items-start'>
                <Icon
                  name='info'
                  className='w-5 h-5 text-yellow-600 mt-0.5 mr-3'
                />
                <div>
                  <h3 className='text-sm font-medium text-yellow-800'>
                    Guest Account
                  </h3>
                  <p className='text-sm text-yellow-700 mt-1'>
                    You're currently using a guest account. Your data is stored
                    locally and won't sync across devices.
                  </p>
                  <p className='text-sm text-yellow-700 mt-2'>
                    Create an account to save your work and access it from any
                    device.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  Email
                </label>
                <div className='mt-1 text-sm text-gray-900'>
                  {user?.email || session?.user?.email || 'Not provided'}
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  Name
                </label>
                <div className='mt-1 text-sm text-gray-900'>
                  {user?.name || session?.user?.name || 'Not provided'}
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700'>
                  Account Type
                </label>
                <div className='mt-1'>
                  <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                    Registered Account
                  </span>
                </div>
              </div>

              {session?.session?.createdAt && (
                <div>
                  <label className='block text-sm font-medium text-gray-700'>
                    Member Since
                  </label>
                  <div className='mt-1 text-sm text-gray-900'>
                    {new Date(session.session.createdAt).toLocaleDateString(
                      'en-US',
                      {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Usage Metrics */}
          <div className='border-t border-gray-200 pt-6'>
            <div className='mb-4'>
              <h3 className='text-lg font-medium text-gray-900'>
                Usage Statistics
              </h3>
              <p className='text-sm text-gray-600'>
                Your activity and data metrics
              </p>
            </div>

            {metricsLoading ? (
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className='bg-gray-50 rounded-lg p-4 animate-pulse'
                  >
                    <div className='h-4 bg-gray-200 rounded w-1/2 mb-2'></div>
                    <div className='h-8 bg-gray-200 rounded w-1/4'></div>
                  </div>
                ))}
              </div>
            ) : metrics ? (
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                <div className='bg-blue-50 rounded-lg p-4 border border-blue-200'>
                  <div className='flex items-center'>
                    <Icon
                      name='folder'
                      className='w-5 h-5 text-blue-600 mr-2'
                    />
                    <h4 className='text-sm font-medium text-blue-900'>
                      Projects
                    </h4>
                  </div>
                  <p className='text-2xl font-bold text-blue-900 mt-1'>
                    {metrics.projects.total}
                  </p>
                  <p className='text-xs text-blue-700'>
                    {metrics.projects.recent} created recently
                  </p>
                </div>

                <div className='bg-green-50 rounded-lg p-4 border border-green-200'>
                  <div className='flex items-center'>
                    <Icon name='file' className='w-5 h-5 text-green-600 mr-2' />
                    <h4 className='text-sm font-medium text-green-900'>
                      Files
                    </h4>
                  </div>
                  <p className='text-2xl font-bold text-green-900 mt-1'>
                    {metrics.files.total}
                  </p>
                  <p className='text-xs text-green-700'>
                    {(metrics.files.totalSize / 1024 / 1024).toFixed(1)}MB total
                    size
                  </p>
                </div>

                <div className='bg-purple-50 rounded-lg p-4 border border-purple-200'>
                  <div className='flex items-center'>
                    <Icon
                      name='grid'
                      className='w-5 h-5 text-purple-600 mr-2'
                    />
                    <h4 className='text-sm font-medium text-purple-900'>
                      Sheets
                    </h4>
                  </div>
                  <p className='text-2xl font-bold text-purple-900 mt-1'>
                    {metrics.sheets.total}
                  </p>
                  <p className='text-xs text-purple-700'>
                    {metrics.sheets.recent} created recently
                  </p>
                </div>

                <div className='bg-orange-50 rounded-lg p-4 border border-orange-200'>
                  <div className='flex items-center'>
                    <Icon
                      name='database'
                      className='w-5 h-5 text-orange-600 mr-2'
                    />
                    <h4 className='text-sm font-medium text-orange-900'>
                      Records
                    </h4>
                  </div>
                  <p className='text-2xl font-bold text-orange-900 mt-1'>
                    {metrics.records.total.toLocaleString()}
                  </p>
                  <p className='text-xs text-orange-700'>
                    {metrics.records.validationRate.toFixed(1)}% validation rate
                  </p>
                </div>

                <div className='bg-teal-50 rounded-lg p-4 border border-teal-200'>
                  <div className='flex items-center'>
                    <Icon
                      name='message-circle'
                      className='w-5 h-5 text-teal-600 mr-2'
                    />
                    <h4 className='text-sm font-medium text-teal-900'>
                      Threads
                    </h4>
                  </div>
                  <p className='text-2xl font-bold text-teal-900 mt-1'>
                    {metrics.threads.total}
                  </p>
                  <p className='text-xs text-teal-700'>
                    {metrics.threads.active} currently active
                  </p>
                </div>

                <div className='bg-indigo-50 rounded-lg p-4 border border-indigo-200'>
                  <div className='flex items-center'>
                    <Icon
                      name='message-square'
                      className='w-5 h-5 text-indigo-600 mr-2'
                    />
                    <h4 className='text-sm font-medium text-indigo-900'>
                      Messages
                    </h4>
                  </div>
                  <p className='text-2xl font-bold text-indigo-900 mt-1'>
                    {metrics.messages.total.toLocaleString()}
                  </p>
                  <p className='text-xs text-indigo-700'>
                    {metrics.messages.recent} in last 24h
                  </p>
                </div>

                <div className='bg-pink-50 rounded-lg p-4 border border-pink-200'>
                  <div className='flex items-center'>
                    <Icon
                      name='activity'
                      className='w-5 h-5 text-pink-600 mr-2'
                    />
                    <h4 className='text-sm font-medium text-pink-900'>
                      Events
                    </h4>
                  </div>
                  <p className='text-2xl font-bold text-pink-900 mt-1'>
                    {metrics.events.total.toLocaleString()}
                  </p>
                  <p className='text-xs text-pink-700'>
                    {metrics.events.recent} in last 24h
                  </p>
                </div>
              </div>
            ) : (
              <div className='text-center py-8'>
                <Icon
                  name='alert-circle'
                  className='w-8 h-8 text-gray-400 mx-auto mb-2'
                />
                <p className='text-sm text-gray-500'>
                  Unable to load usage statistics
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className='border-t border-gray-200 pt-6'>
            <div className='flex items-center justify-between'>
              <div>
                <h3 className='text-sm font-medium text-gray-900'>
                  Account Actions
                </h3>
                <p className='text-sm text-gray-600'>
                  Manage your session and account
                </p>
              </div>
              <button
                onClick={handleSignOut}
                disabled={isSigningOut}
                className='inline-flex items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50'
              >
                <Icon name='logout' className='w-4 h-4 mr-2' />
                {isSigningOut
                  ? 'Signing Out...'
                  : isGuest
                    ? 'Clear Session'
                    : 'Sign Out'}
              </button>
            </div>
          </div>

          {isGuest && (
            <div className='border-t border-gray-200 pt-6'>
              <div className='text-center'>
                <h3 className='text-sm font-medium text-gray-900 mb-2'>
                  Want to save your work?
                </h3>
                <p className='text-sm text-gray-600 mb-4'>
                  Create an account to sync your projects and access them from
                  anywhere.
                </p>
                <button
                  onClick={() => {
                    clearAuthToken()
                    window.location.href = '/'
                  }}
                  className='inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                >
                  Create Account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
